// What to ask, on each kind of board (Features 4, part 3): plain requests a first-day user would type, for that
// kind, each one something the board's own tools do on Claude Code and Codex alike (worker/mcp.ts: add_widget,
// write_page, write_sheet, write_slides, write_page and ask_plan on a plan, edit_map, add_task and read_calendar,
// write_post, write_deal and update_board's stages). A click puts the words in the chat's box, never sends them.

import type { BoardKind } from "../../shared/types";

export interface KindStart {
  /** what the board is for, in a line */
  line: string;
  /** asks for its empty board, two or three */
  empty: string[];
  /** asks for the chat while the board has something on it */
  busy: string[];
}

export const KIND_START: Record<BoardKind, KindStart> = {
  canvas: {
    line: "Widgets anywhere, built with your agent and kept current.",
    empty: ["Add the weather for London", "Add a to-do list for this week", "Add the Hacker News front page"],
    busy: ["What does this board tell me today?", "Organize this board into sections", "Add the weather for my city"],
  },
  doc: {
    line: "Pages inside pages, with to-dos and live widgets, written with your agent.",
    empty: ["Write a page that plans my week", "Make a meeting notes page with to-dos", "Start a page of ideas for my next project"],
    busy: ["Summarize this page", "Turn this page into a checklist", "Add a page of next steps"],
  },
  sheet: {
    line: "Rows, columns and formulas, filled in with your agent.",
    empty: ["Make a monthly budget with totals", "Set up a sheet to track my invoices", "Make a habit tracker for this month"],
    busy: ["Add a total under each column", "Sort this tab by the first column", "What stands out in this sheet?"],
  },
  slides: {
    line: "A deck you and your agent write, ready to present or share.",
    empty: ["Draft a five-slide pitch for my product", "Make a deck for my weekly update", "Add an agenda slide after the title"],
    busy: ["Tighten the words on every slide", "Add a closing slide with next steps", "Make the title slide stand out"],
  },
  plan: {
    line: "Plan a big build with your agent, then watch it get built.",
    empty: ["Break my build into steps", "Help me plan a new feature", "Write the goal and what done looks like"],
    busy: ["What's left to do on this plan?", "What is waiting on me?", "Break the next step into tasks"],
  },
  files: {
    line: "Your project folders as notes you click through, linked and searchable.",
    empty: ["Find my notes about pricing", "Summarize what's in my project folder", "List the to-dos in my notes"],
    busy: ["Find my notes about pricing", "Summarize the note I have open", "List the to-dos in my notes"],
  },
  mindmap: {
    line: "Ideas as a map that you and your agent grow.",
    empty: ["Add five ideas around the main idea", "Map out my next project", "Brainstorm names for my product"],
    busy: ["Break each idea into smaller ones", "Add five more ideas", "Group these ideas into themes"],
  },
  agenda: {
    line: "Your meetings, posts and tasks in one place.",
    empty: ["What can I do on this calendar?"],
    busy: ["What's on this week?", "Find an hour for deep work on Thursday", "Move my 3pm to tomorrow"],
  },
  calendar: {
    line: "Plan, draft and track every post.",
    empty: ["Draft three posts for this week", "Give me ten post ideas", "Plan a post for every weekday"],
    busy: ["Draft three posts for this week", "What's planned for next week?", "Turn my newest idea into a draft"],
  },
  custom: {
    line: "Ask your agent to build this board.",
    empty: ["Build me a content pipeline with four stages", "Build a client intake board", "Build a review queue for my team"],
    busy: ["Add a filter by stage", "Move the three oldest records to Done", "Make it easier to add a record"],
  },
  pipeline: {
    line: "Deals from first hello to won, with follow-ups.",
    empty: ["Add my first three clients", "Rename the columns to fit how I sell"],
    busy: ["What should I follow up on this week?", "Add my next client", "Which deals have gone quiet?"],
  },
};

/** The three asks an empty chat offers for the board in view. */
export function chatPrompts(kind: BoardKind, hasContent: boolean): string[] {
  const k = KIND_START[kind] ?? KIND_START.canvas;
  return (hasContent ? k.busy : k.empty).slice(0, 3);
}
